import { AccountCircle } from "@material-ui/icons";
import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Link from "./Link";
import ListSubheader from "@material-ui/core/ListSubheader";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import React, { useState, useEffect } from "react";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import _ from "lodash";
import clsx from "clsx";
import config from "../config";
import logo from "../assets/images/logo.svg";
import { makeStyles } from "@material-ui/core/styles";
import signOut from "../modules/signOut";
import { useUser } from "../hooks/user";
import { withRouter } from "react-router";
import { compactNumber } from "../utils/compactNumber";

import { useChatContext } from "stream-chat-react";

const height = 60;

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    height: height,
    boxShadow: "none"
  },
  commitRef: {
    color: theme.palette.muted.main
  },
  toolbar: theme.mixins.toolbar,
  title: {
    marginTop: theme.spacing(2),
    flexGrow: 1
  },
  titleUnauthed: {
    textAlign: "center"
  },
  logo: {
    height: 45,
    marginRight: theme.spacing(2)
  },
  signInButton: {
    color: theme.palette.secondary.dark,
    backgroundColor: theme.palette.light.main
  },
  userIcon: {
    marginLeft: theme.spacing(2)
  },
  menuItem: {
    padding: 10,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    display: "block",
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

export default function AdminAppBar() {
  const classes = useStyles();
  const { user, userAuthed, userUnauthed } = useUser();
  return (
    <AppBar position="static" color="transparent" className={classes.appBar}>
      <Container maxWidth="xl" disableGutters>
        <Toolbar
          className={clsx(
            classes.toolbar,
            userUnauthed && classes.titleUnauthed
          )}
        >
          <Link to="/" color="inherit">
            <Typography variant="h6" className={classes.title}>
              <img alt="logo" src={logo} className={classes.logo} />
            </Typography>
          </Link>

          {userAuthed && <Routes />}
          {userAuthed ? <Authed user={user} /> : null}
        </Toolbar>
      </Container>
    </AppBar>
  );
}

const moneyToolsRoutes = [
  ["Invoices", "/invoices"],
  ["Payouts", "/payouts"],
  ["Statements", "/statements"],
  ["Agg. Payments", "/aggregated-payments"]
];

const boomCrmRoutes = [
  ["Phone Numbers", "/provisioned-phone-numbers"],
  ["Workflow Analytics", "/workflow-analytics"],
  ["Schedule Health", "/workflow-analytics/schedule-health"]
];

const agentsRoutes = [
  ["Agent", "/agent"],
  ["BSS", "/bss"]
];

const routes = [
  ["Affiliates", "/affiliates"],
  ["Partners", "/partners"],
  ["Customers", "/customers"],
  ["Applications", "/applications"],
  ["Enrollments", "/reporting-account-enrollments"],
  ["Furnishments", "/furnishments"],
  ["Verifications", "/enrollment-verifications"],
  ["Payment Verification", "/payment-verifications"],
  ["Chime Verifications", "/chime-verifications"],
  ["KYB Verification", "/kyb-verifications"],
  ["Bulk Uploads", "/bulk-uploads"],
  ["Universal Exclusions", "/universal-exclusions"],
  ["Chats", "/chats"],
  ["PMS Request Logs", "/pms-request-logs"],
  ["Applicant Alerts", "/applicant-alerts"]
];
export const navRoutes = [...routes, ...agentsRoutes];

function DropdownMenu({ label, items }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <Link
        className={classes.menuItem}
        onClick={(e) => {
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
        to="#"
        style={{ cursor: "pointer" }}
      >
        <Typography>{label}</Typography>
      </Link>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {items.map((item) =>
          Array.isArray(item) ? (
            <MenuItem
              key={item[1]}
              component={Link}
              to={item[1]}
              onClick={() => setAnchorEl(null)}
            >
              {item[0]}
            </MenuItem>
          ) : (
            <React.Fragment key={item.header}>
              <ListSubheader disableSticky>{item.header}</ListSubheader>
              {item.items.map(([title, href]) => (
                <MenuItem
                  key={href}
                  component={Link}
                  to={href}
                  onClick={() => setAnchorEl(null)}
                  style={{ paddingLeft: 32 }}
                >
                  {title}
                </MenuItem>
              ))}
            </React.Fragment>
          )
        )}
      </Menu>
    </>
  );
}

function Routes() {
  const [unreadMessages, setUnreadMessages] = useState();
  const { client } = useChatContext();
  const classes = useStyles();

  const { user } = useUser();
  const streamChatId = user?.streamChatId;

  useEffect(() => {
    if (client) {
      const fetchUnread = () => {
        return client
          .getUnreadCount(streamChatId)
          .then((res) => {
            setUnreadMessages(res);
          })
          .catch(() => {
            // Do nothing
          });
      };

      fetchUnread();

      // chime on new messages

      const events = [
        client.on("notification.message_new", () => {
          // Send notification via browser if permissions are granted
          if (Notification.permission === "granted")
            new Notification("New message", {
              body: "You have a new message in your chat.",
              icon: "/favicon.ico"
            });

          fetchUnread();
        }),
        client.on("message.new", () => {
          fetchUnread();
        }),
        client.on("message.read", () => {
          fetchUnread();
        })
      ];

      return () => {
        events.forEach((event) => event.unsubscribe());
      };
    }
  }, [streamChatId]);

  // Insert dropdowns after first 3 top-level items (Affiliates, Partners, Customers)
  const beforeDropdowns = routes.slice(0, 3);
  const afterDropdowns = routes.slice(3);

  return (
    <>
      {beforeDropdowns.map(([title, href]) => (
        <Link key={href} to={href} className={classes.menuItem}>
          <Typography>{title}</Typography>
        </Link>
      ))}
      <DropdownMenu label="Money Tools" items={moneyToolsRoutes} />
      <DropdownMenu label="BoomCRM" items={boomCrmRoutes} />
      {afterDropdowns.map(([title, href]) => (
        <Link key={href} to={href} className={classes.menuItem}>
          <Typography
            style={{
              position: "relative"
            }}
          >
            {title}

            {title === "Chats" && unreadMessages?.total_unread_count ? (
              <div
                style={{
                  position: "absolute",
                  top: -6,
                  right: -16,
                  backgroundColor: "rgba(255, 0, 0, 0.5)",
                  borderRadius: 4,
                  color: "white",
                  fontWeight: "bold",
                  padding: "0 4px",
                  fontSize: 10
                }}
              >
                {compactNumber(unreadMessages.total_unread_count)}
              </div>
            ) : null}
          </Typography>
        </Link>
      ))}
      <DropdownMenu label="Agents" items={agentsRoutes} />
    </>
  );
}

function _Authed({ user }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);
  const close = _.bind(setAnchorEl, null);

  function handleSignOut() {
    close();
    signOut();
  }

  return (
    <div>
      <Button color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
        {user && user.firstName}
        <AccountCircle className={classes.userIcon} />
      </Button>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        keepMounted
        open={isOpen}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          component={Link}
          to="/account/change-password"
          onClick={close}
        >
          Change password
        </MenuItem>
        <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
        <MenuItem
          className={classes.commitRef}
          onClick={() =>
            window.open(
              `https://github.com/boompay/boompay-admin-web/commit/${config.commitRef}`
            )
          }
        >
          Version: {config.commitRef}
        </MenuItem>
      </Menu>
    </div>
  );
}
const Authed = withRouter(_Authed);
